import React from "react";
import BannerImg from "../../assets/images/banner/act-advantage.png";
import EnterpriseServicePage from "../../components/services/enterprise-service-page";

const schemas = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Legacy Modernization",
    serviceType: "Application Modernization",
    description:
      "Legacy modernization services that preserve business value, stabilize current systems and evolve enterprise platforms safely.",
    provider: {
      "@type": "Organization",
      name: "Avani Cimcon Technologies",
      url: "https://www.avanicimcon.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.avanicimcon.com/it-consulting-services" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Legacy Modernization",
        item: "https://www.avanicimcon.com/services/legacy-modernization",
      },
    ],
  },
];

const LegacyModernization = () => (
  <EnterpriseServicePage
    title="Legacy Modernization for Enterprise Systems, Platforms & AI Readiness | Avani Cimcon"
    description="Legacy modernization services from Avani Cimcon preserve proven business logic, stabilize mission-critical systems and create a safe path to integration and AI enablement."
    keywords="legacy modernization, application modernization, enterprise software engineering, modernization framework, preserve stabilize modernize integrate enable AI, managed evolution"
    canonical="https://www.avanicimcon.com/services/legacy-modernization"
    ogImage="https://www.avanicimcon.com/images/banner/act-advantage.png"
    hero={{
      eyebrow: "Legacy Modernization",
      heading: "Legacy modernization that preserves value before it changes anything",
      lead:
        "The best modernization programs respect what the business already depends on. This page turns the company’s long engineering history into a practical framework for safe evolution.",
      tags: ["Assess", "Preserve", "Stabilize", "Modernize"],
      primaryCta: { label: "Talk to Us", href: "/contact-us" },
      secondaryCta: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
      image: BannerImg,
      panelKicker: "Preservation first.",
      panelTitle: "Modernization should be incremental, not destructive.",
      panelText:
        "Avani Cimcon's framework keeps business logic intact while making room for integration, support and AI enablement.",
      stats: [
        { value: "1992", label: "Enterprise software foundation" },
        { value: "Enterprise", label: "Mission-critical focus" },
        { value: "Global", label: "International delivery experience" },
      ],
    }}
    whatItIs={{
      eyebrow: "What It Is",
      title: "A practical modernization service for systems that still matter",
      lead: "Modernization is about evolution, not replacement at any cost.",
      paragraphs: [
        "Legacy modernization helps organizations update systems, architecture and integrations while keeping the business outcomes already embedded in the platform. That matters when the application still supports operations, revenue or customer service.",
        "Avani Cimcon has spent decades building and maintaining enterprise systems across product, vertical and integration use cases. That history makes the modernization conversation more grounded because the team understands both old stacks and the value they still carry.",
      ],
    }}
    whoItIsFor={{
      title: "Who it is for",
      lead: "Enterprises that need to improve older systems without losing control.",
      items: [
        "Organizations with mission-critical legacy platforms",
        "Teams that need to reduce technical debt in stages",
        "Businesses that want to modernize and add AI without starting from zero",
      ],
    }}
    businessProblems={{
      eyebrow: "Problems Solved",
      title: "The modernization problems the framework is designed to solve",
      lead: "Reduce risk while keeping the business moving.",
      items: [
        {
          title: "Aging code and architecture",
          text: "The platform still works, but the structure makes change slower and more expensive than it should be.",
        },
        {
          title: "Integration friction",
          text: "Older systems need to connect to newer tools, data sources and APIs without breaking.",
        },
        {
          title: "Knowledge risk",
          text: "The original design intent is only partly known, so change must be handled carefully.",
        },
      ],
    }}
    scenarios={{
      eyebrow: "Typical Scenarios",
      title: "Typical enterprise modernization scenarios",
      lead: "These are the patterns that show up when a company wants to evolve safely.",
      items: [
        { title: "Mainframe or older platform support", text: "Modernize critical functionality without shutting down the current system." },
        { title: "Browser or UI refresh", text: "Refresh user experience while protecting the underlying business logic." },
        { title: "Data exchange improvement", text: "Use integration and middleware to connect legacy and modern applications." },
        { title: "AI readiness", text: "Prepare the estate so AI can be layered on top of stable systems." },
      ],
    }}
    deliveryApproach={{
      eyebrow: "Modernization Framework",
      title: "Avani Cimcon's modernization framework",
      lead: "A simple sequence that keeps the business in control of change.",
      paragraphs: [
        "This framework is intentionally reusable across the site because it describes the company's preferred modernization posture. The sequence makes it easier to preserve the enterprise value already inside the application estate.",
        "The emphasis is not on replacing the platform as quickly as possible. The emphasis is on managed evolution that respects operational reality.",
      ],
      callout: {
        title: "The framework",
        items: [
          "Assess the current estate and risk profile",
          "Preserve business logic and proven workflows",
          "Stabilize the system before larger change",
          "Modernize components in manageable steps",
          "Integrate newer platforms and services",
          "Enable AI where it adds measurable value",
          "Manage evolution as an ongoing operating model",
        ],
      },
    }}
    industries={{
      eyebrow: "Industries Served",
      title: "Modernization is especially valuable in industries with long-lived systems",
      lead: "The company’s strongest verticals show where careful modernization matters most.",
      items: [
        { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Reservation and booking engines that must keep pace." },
        { label: "Healthcare", href: "/healthcare", text: "Operational and patient workflows with high continuity needs." },
        { label: "Insurance", href: "/insurance", text: "Policy and claims platforms with heavy process logic." },
        { label: "Legal", href: "/legal", text: "Document and matter systems that require precision." },
        { label: "DXchange", href: "/dxchange", text: "Integration and data exchange across old and new systems." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules and decision logic that can be modernized safely." },
      ],
    }}
    aiOpportunities={{
      eyebrow: "Related AI Opportunities",
      title: "Modernization creates the conditions for AI to be useful",
      lead: "AI becomes easier to adopt once the estate is stable and integrated.",
      items: [
        {
          title: "Enterprise AI Enablement",
          text: "Layer AI onto the systems that modernization has made safer to change.",
          href: "/services/enterprise-ai-enablement",
          linkLabel: "See the AI service",
        },
        {
          title: "Enterprise AI Overview",
          text: "Understand how the company frames AI as a governed capability.",
          href: "/enterprise-ai",
          linkLabel: "Open the AI hub",
        },
        {
          title: "Managed Services",
          text: "Keep the estate stable while the modernization roadmap progresses.",
          href: "/services/managed-services",
          linkLabel: "See managed services",
        },
      ],
    }}
    relatedProducts={{
      eyebrow: "Related Products",
      title: "Products that show how the company builds on legacy and integration strength",
      lead: "The heritage products below support the modernization story through proof, not claims.",
      items: [
        { label: "DXchange", href: "/dxchange", text: "Enterprise data exchange and integration." },
        { label: "CMS", href: "/cms", text: "Content and portal delivery experience." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Business rules and decisioning platform." },
      ],
    }}
    relatedServices={{
      eyebrow: "Related Services",
      title: "Services that sit naturally beside modernization",
      lead: "Use the service cluster to plan support, embedded delivery and AI enablement together.",
      items: [
        { label: "Managed Services", href: "/services/managed-services", text: "Support, maintenance and controlled upgradation." },
        { label: "Dedicated Engineering Teams", href: "/services/dedicated-engineering-teams", text: "Embedded capacity for long-running work." },
        { label: "Enterprise AI Enablement", href: "/services/enterprise-ai-enablement", text: "Add AI where it can be governed properly." },
      ],
    }}
    cta={{
      eyebrow: "Talk to Us",
      title: "Want a modernization plan that protects what already works?",
      text: "Let's discuss the estate, the risks and the path to change before anything gets rewritten.",
      primary: { label: "Talk to Us", href: "/contact-us" },
      secondary: { label: "Discuss Managed Services", href: "/services/managed-services" },
    }}
    schemas={schemas}
  />
);

export default LegacyModernization;
